import { useEffect, useState } from "react";
import type { GuideEvent, GuideGap } from "@shared/epg-guide";
import { getBinding, getScreenId } from "../lib/binding";
import { fetchBounded } from "../lib/boundedFetch";

/**
 * This room's line-up, with the guide attached (`TV-4`).
 *
 * A second request rather than a block of the premise config, for the same
 * reason `useRoomOffers` is: a config is edited by an operator and pushed over
 * the realtime channel, while a schedule moves on its own — three ingest runs a
 * day upstream, and a programme that started while the television was showing
 * the home screen. Folding the guide into the config would mean either a stale
 * "on now" or a config refetch every few minutes for every premise, channels or
 * not.
 *
 * There is no central API client in this app. Every call is a bare `fetch` with
 * the screen token from the pairing binding, and this is that convention, not a
 * shortcut.
 *
 * ## 🚨 The payload carries no `live` flag, by design
 *
 * The server read is cached in ten-minute bins ([[epg-guide]]), and a cached
 * boolean is wrong within seconds of being written. So the wire carries
 * *instants* only and every surface calls `eventAt(events, now)` against its own
 * clock — see `../schemes/_shared/guide/lineup.ts`. Do not add a `live` field
 * here to save the caller the work.
 *
 * ## Tolerant on purpose
 *
 * The route is young. Unknown fields are ignored and missing ones degrade to a
 * sane default rather than to an exception, because the failure mode this
 * protects against is a television in a hotel room rendering nothing at all
 * after a server deploy adds a field. The one thing that is NOT tolerated is a
 * body with no `channels` array: that is a captive portal or a proxy answering
 * for the API, and it must not be mistaken for "this room has no channels".
 */

/**
 * One channel of a room's line-up.
 *
 * `events` and `gaps` are exactly the `ChannelGuide` shapes from
 * `@shared/epg-guide` — resolved, non-overlapping, `startScheduled` ascending —
 * so the guide model on the client is the same one the CMS panel uses and the
 * two cannot disagree at a boundary.
 */
export interface TvLineupChannel {
  /** Stable id within the line-up. Falls back to the channel asset id. */
  id: string;
  /**
   * The number a guest types on the remote.
   *
   * Positional when the server does not state one, so a line-up always has
   * something to type. A room whose operator has numbered its channels gets
   * those numbers; one that has not still gets 1..n rather than a keypad that
   * does nothing.
   */
  number: number;
  title: string;
  logoUrl?: string;
  /** The catalogue channel whose EPG these events belong to. */
  channelAssetId?: string;
  /**
   * The catalogue asset the play endpoint resolves into a manifest and a
   * licence (`/screens/:id/play/:assetId`). Absent means the channel is in the
   * line-up and cannot be tuned here — which the screen SAYS rather than
   * rendering a black rectangle.
   */
  contentAssetId?: string;
  /**
   * A manifest the server resolved for us already. Only ever set for clear
   * content; a protected channel resolves through the play endpoint so its
   * five-minute token is minted at the instant it is used.
   */
  url?: string;
  events: GuideEvent[];
  gaps: GuideGap[];
}

export interface TvLineup {
  /** The window the guide covers, echoed for the "guide ends here" case. */
  window?: { from: string; to: string };
  channels: TvLineupChannel[];
}

/**
 * How often to re-ask.
 *
 * Ten minutes, which is the server's own cache bin (`EPG_BIN_SECONDS`, 600):
 * asking faster cannot produce a fresher answer, it only produces a request
 * that is served from the same bin. What keeps "on now" correct between polls
 * is not this timer but the device clock — `eventAt` is re-evaluated on
 * `useNow`'s tick, so a programme changes over on time even if the payload is
 * nine minutes old.
 */
export const LINEUP_POLL_MS = 10 * 60_000;

/** Backoff for an API that is not answering. Same shape as `useRoomOffers`'. */
const RETRY_BASE_MS = 5_000;
const RETRY_MAX_MS = 60_000;

/**
 * Normalise whatever the route sent into a line-up, or `null` when the body is
 * not one.
 *
 * Exported for its test: this function is the whole tolerance policy, and the
 * policy is worth pinning down where a change to it is visible.
 */
export function normaliseLineup(raw: unknown): TvLineup | null {
  if (!raw || typeof raw !== "object") return null;
  const body = raw as {
    channels?: unknown;
    window?: unknown;
  };
  if (!Array.isArray(body.channels)) return null;

  const channels: TvLineupChannel[] = [];
  body.channels.forEach((entry, index) => {
    if (!entry || typeof entry !== "object") return;
    const c = entry as Record<string, unknown>;
    const channelAssetId = str(c.channelAssetId);
    const id = str(c.id) ?? channelAssetId;
    // No id at all is not a channel we can hold state about - it could not be
    // tuned to, kept as the last channel, or told apart from its neighbour.
    if (!id) return;
    const number = int(c.number);
    channels.push({
      id,
      // Positional fallback is 1-based: a remote has no channel 0, and typing
      // "0" first is how a guest reaches a two-digit number.
      number: number != null && number > 0 ? number : index + 1,
      // `channelTitle` is what `ChannelGuide` calls it. Accept both rather than
      // rendering an empty row because the route picked the other word.
      title: str(c.title) ?? str(c.channelTitle) ?? "",
      logoUrl: str(c.logoUrl),
      channelAssetId,
      contentAssetId: str(c.contentAssetId) ?? channelAssetId,
      url: str(c.url),
      events: Array.isArray(c.events) ? (c.events as GuideEvent[]) : [],
      gaps: Array.isArray(c.gaps) ? (c.gaps as GuideGap[]) : [],
    });
  });

  const win = body.window as { from?: unknown; to?: unknown } | undefined;
  const from = str(win?.from);
  const to = str(win?.to);
  return {
    window: from && to ? { from, to } : undefined,
    channels,
  };
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() !== "" ? v : undefined;
}

function int(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? Math.floor(v) : null;
}

/**
 * The room's line-up, or `undefined` until one has arrived.
 *
 * `undefined` is load-bearing and means **not answered yet**, which the screen
 * says out loud instead of drawing an empty channel list. An empty `channels`
 * array is a different statement — "this room has no channels" — and the two
 * must not collapse into one.
 *
 * A failed request keeps the LAST GOOD line-up rather than blanking the guide.
 * A hotel Wi-Fi blip must not take live television away from a guest who is
 * already watching it: the stream itself is unaffected by a failed guide read,
 * so emptying the list would break the one thing that was still working.
 *
 * `preview` short-circuits the whole thing for the design preview
 * (`?scheme=<key>&on=guide`), which has no pairing and no backend. A bound
 * television never receives one.
 */
/**
 * @param enabled Whether this caller wants the feed at all. Defaults to true,
 *   because the guide SCREEN is only ever mounted when the guide is wanted.
 *   `SchemeRouter` passes it explicitly: it mounts on every page of every
 *   template, so an ungated read there would have every care premise in the
 *   estate polling a channel line-up it declares no knob for. Same shape of
 *   mistake as `showCast`, which really did switch Google Cast on in care
 *   rooms because `knobs.toggle()` fails visible.
 */
export function useTvLineup(
  preview?: TvLineup,
  enabled = true
): TvLineup | undefined {
  const [lineup, setLineup] = useState<TvLineup | undefined>(undefined);
  // The binding, read once per mount. A television that pairs while the guide
  // is open re-mounts the screen anyway (App remounts on binding change), so
  // watching it here would only add a second reason to re-run the effect.
  const token = preview ? undefined : getBinding()?.screenToken;

  useEffect(() => {
    setLineup(undefined);
    if (!enabled) return;
    if (preview || !token) return;
    const screenId = getScreenId();
    let alive = true;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = (ms: number) => {
      timer = setTimeout(() => {
        timer = undefined;
        void load();
      }, ms);
    };

    async function load(): Promise<void> {
      try {
        // Bounded: an unanswered request would never settle, so this `catch`
        // would never run and the poll below would never be rescheduled.
        const res = await fetchBounded(
          `/api/foyer/screens/${encodeURIComponent(screenId)}/tv/guide`,
          { headers: { "X-Screen-Token": token! } }
        );
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const next = normaliseLineup(await res.json());
        if (!alive) return;
        failures = 0;
        // A body without the array is a proxy answering for the API, not a
        // room with no channels - keep the last good line-up.
        if (next) setLineup(next);
        schedule(LINEUP_POLL_MS);
      } catch {
        if (!alive) return;
        failures += 1;
        schedule(Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (failures - 1)));
      }
    }

    void load();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [token, preview, enabled]);

  return preview ?? lineup;
}
